// FileReviewDialog.js
// Reviewing one uploaded file (Spec 2 section 6.4): a full-size dialog with the
// file itself on the left (FilePane) and, on the right, what Decode decided
// about it, which is the file view or one table's unit view.
//
//   header   name, status, profile, upload time, reporting date, approver, the
//            upload's attributes, Download, Close
//   left     the file pane: sheets or pages, with the tables painted over them
//   right    the file view: the file's problems; the check it is in; the
//            file's steps (FileSteps); its tables (UnitCards). Or, for one
//            table, the unit view (UnitView), with the file pane focused on it.
//   footer   Delete file, Submit (while the check's problems stand), Back to
//            the file (in the unit view), Approve file, Close
//
// While open, the dialog follows the file document and the file's unit
// documents (one query on `file_id`), its profile (for whether Approve file may
// approve every table at once) and, while the file is in a check, the batch.
// It closes itself, with a notice, when the file is deleted.
//
// Every change goes through services/cleaningGestures.js, whose transactions are
// the authority on whether it may be made; a refusal is shown as a toast in its
// own words, and any other failure as a fixed sentence (gestureFailureMessage).
// A change that would un-approve the file (or take it out of its check) is
// confirmed first. When the documents show that a confirmation's target has
// become busy or approved meanwhile, the confirmation is closed with a notice
// and its change is not sent, then or later. Only the affected confirmation
// closes: a sibling table's processing never closes another table's. The file
// view and the unit view hand their changes to runFileChange and
// runUnitApproval, which answer whether the change was made, so an editor keeps
// its draft until then.
//
// The dialog also hosts the sheet workspace, opened from the file view ("Open
// sheet") or the unit view ("Adjust"). A workspace that closes itself because
// the file stopped being editable hands its unsaved draft here, and gets it back
// the next time the same sheet is opened.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { fileEditBlock, unitEditBlock } from '../../../utils/ingestionStatus';
import {
  approveFile,
  deleteFile,
  GestureRefused,
  gestureFailureMessage,
  NO_INGEST_UNIT_LEFT,
  needsRewindConfirmation,
  REWIND_CONFIRMATION,
  submitFile,
} from '../../../services/cleaningGestures';
import { downloadFile } from '../../../services/fileDownloads';
import { downloadFailureMessage } from '../../../services/api_utils';
import { deleteConfirmationText, fileDeleteBlock } from '../../../utils/fileDeletion';
import { isProfileLearned } from '../../../utils/profileLearning';
import { mergeSnapshotDocuments } from '../../../utils/liveSnapshots';
import { formatDisplayValue } from '../../../utils/tagValueUtils';
import { formatDateOnly, formatTimestamp } from '../../../utils/timestamps';
import { countNoun } from '../../../utils/pluralise';
import { formatText } from '../../common/TextFormatterProvider';
import useFileBatch from '../../../hooks/useFileBatch';
import FilePane from './FilePane';
import FileSteps from './FileSteps';
import UnitCards from './UnitCards';
import UnitView from './UnitView';
import SheetWorkspace from './SheetWorkspace';
import FieldsDialog from './FieldsDialog';
import BatchPanel from './BatchPanel';
import { verdictRowsText } from './UnitStepFrame';
import {
  approvalLine,
  FileStatusIcon,
  fileStatusColour,
  fileStatusLabel,
} from './ingestionStatusDisplay';

export const FILE_DELETED_NOTICE = 'This file was deleted while it was open.';
export const UNIT_REMOVED_NOTICE = 'That table was removed from the file.';

// A file verdict as a banner: its message, the tables it names and their rows.
function VerdictBanner({ verdict, units }) {
  const unitNames = (verdict.units || []).map((unitId) => units.find((unit) => unit.id === unitId)?.name || 'a table');
  const rows = verdictRowsText(verdict);
  return (
    <Alert severity={verdict.level === 'fail' ? 'error' : 'warning'} sx={{ mb: 1 }}>
      <Typography variant="body2" fontWeight={600}>{verdict.message}</Typography>
      {unitNames.length > 0 && (
        <Typography variant="caption" sx={{ display: 'block' }}>
          In {unitNames.join(', ')}
          {rows ? ` · ${rows}` : ''}
        </Typography>
      )}
    </Alert>
  );
}

function FileReviewDialog({
  open,
  companyId,
  tableId,
  table,
  fileId,
  initialUnitId = null,
  onClose,
  onNotice,
}) {
  const { currentUser } = useAuth();
  const tableName = formatText(table.name);
  const [loadAttempt, setLoadAttempt] = useState(0);
  // The host's callbacks as they are now, for the listeners to call.
  const hostRef = useRef({ onClose, onNotice });
  hostRef.current = { onClose, onNotice };

  // ── The live documents ────────────────────────────────────────────────────
  const [fileState, setFileState] = useState({ file: null, error: false });
  useEffect(() => {
    if (!open) return undefined;
    const fileRef = doc(db, `users/${companyId}/tables/${tableId}/files/${fileId}`);
    return onSnapshot(fileRef, (snapshot) => {
      if (!snapshot.exists()) {
        hostRef.current.onNotice(FILE_DELETED_NOTICE);
        hostRef.current.onClose();
        return;
      }
      setFileState({ file: { id: snapshot.id, ...snapshot.data() }, error: false });
    }, (error) => {
      console.error('Error watching the file under review:', error);
      setFileState((previous) => ({ ...previous, error: true }));
    });
  }, [open, companyId, tableId, fileId, loadAttempt]);
  const file = fileState.file;

  const [unitsState, setUnitsState] = useState({ units: [], loaded: false, error: false });
  useEffect(() => {
    if (!open) return undefined;
    // Only what each snapshot changed is converted: a unit carries its sample
    // rows, statistics and preview, and a large file has many units.
    let unitsById = new Map();
    const unitsQuery = query(
      collection(db, `users/${companyId}/tables/${tableId}/units`),
      where('file_id', '==', fileId),
    );
    return onSnapshot(unitsQuery, (snapshot) => {
      unitsById = mergeSnapshotDocuments(unitsById, snapshot);
      // In the file's order: sheets as in the workbook, tables as in each sheet.
      const units = Array.from(unitsById.values()).sort((left, right) => left.order - right.order);
      setUnitsState({ units, loaded: true, error: false });
    }, (error) => {
      console.error('Error watching the units of the file under review:', error);
      setUnitsState((previous) => ({ ...previous, error: true }));
    });
  }, [open, companyId, tableId, fileId, loadAttempt]);
  const units = unitsState.units;

  const profileId = file?.data_profile_id || null;
  const [profileState, setProfileState] = useState({ profileId: null, profile: null });
  useEffect(() => {
    if (!open || !profileId) return undefined;
    const profileRef = doc(db, `users/${companyId}/tables/${tableId}/data_profiles/${profileId}`);
    return onSnapshot(profileRef, (snapshot) => {
      setProfileState({ profileId, profile: snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null });
    }, (error) => {
      console.error('Error watching the profile of the file under review:', error);
    });
  }, [open, companyId, tableId, profileId]);
  const profile = profileState.profileId === profileId ? profileState.profile : null;

  const { batch, fileNames, error: batchError } = useFileBatch(companyId, tableId, file);

  // ── What is open ──────────────────────────────────────────────────────────
  const [unitId, setUnitId] = useState(initialUnitId);
  // The sheet workspace: { sheet, focusRegionId }, or null.
  const [workspace, setWorkspace] = useState(null);
  // Sheet name -> { draft, seed }: an unsaved workspace draft set aside when the
  // workspace closed itself, given back when that sheet is opened again.
  const [setAsideSheetDrafts, setSetAsideSheetDrafts] = useState({});
  const [fieldsUnitId, setFieldsUnitId] = useState(null);
  // { title, paragraphs, confirmLabel, run, successMessage, blockOf(file, batch, units),
  //   settle(made) }: settle answers the change's caller once it is sent or not.
  const [confirmation, setConfirmation] = useState(null);
  const [noIngestOpen, setNoIngestOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [approvingFile, setApprovingFile] = useState(false);
  const [submittingFile, setSubmittingFile] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const reviewPaneRef = useRef(null);

  const showToast = useCallback((message, severity = 'error') => setToast({ message, severity }), []);
  const showNotice = useCallback((message) => setToast({ message, severity: 'info' }), []);

  const currentUnit = unitId ? units.find((unit) => unit.id === unitId) || null : null;
  const fieldsUnit = fieldsUnitId ? units.find((unit) => unit.id === fieldsUnitId) || null : null;

  // The table shown in the unit view was removed (its sheet was re-read, say).
  useEffect(() => {
    if (unitId && unitsState.loaded && !currentUnit) {
      setUnitId(null);
      showNotice(UNIT_REMOVED_NOTICE);
    }
  }, [unitId, unitsState.loaded, currentUnit, showNotice]);

  // Moving between the file view and a unit view starts the pane at its top.
  useEffect(() => {
    if (reviewPaneRef.current) reviewPaneRef.current.scrollTop = 0;
  }, [unitId]);

  // A confirmation whose change can no longer be made closes, unsent.
  useEffect(() => {
    if (!confirmation || !file) return;
    const block = confirmation.blockOf(file, batch, units);
    if (block) {
      setConfirmation(null);
      confirmation.settle(false);
      showNotice(`${block} Your change was not sent.`);
    }
  }, [confirmation, file, batch, units, showNotice]);

  // ── Running changes ───────────────────────────────────────────────────────
  // Runs one gesture and answers whether it was made. A refusal is shown in its
  // own words; leaving a file with nothing to ingest has a dialog of its own.
  const runGesture = useCallback(async (run, successMessage) => {
    try {
      await run();
      if (successMessage) showToast(successMessage, 'success');
      return true;
    } catch (error) {
      if (error instanceof GestureRefused && error.message === NO_INGEST_UNIT_LEFT) {
        setNoIngestOpen(true);
      } else {
        showToast(gestureFailureMessage(error));
      }
      return false;
    }
  }, [showToast]);

  // A change to the file's own decisions, answering whether it was made.
  // `options.confirmation` asks first in any case; a file that is approved or
  // being checked is always asked about.
  const runFileChange = useCallback((run, options = {}) => {
    const rewinds = needsRewindConfirmation(file);
    const paragraphs = [];
    if (options.confirmation) paragraphs.push(options.confirmation);
    if (rewinds) paragraphs.push(REWIND_CONFIRMATION);
    if (paragraphs.length === 0) return runGesture(run, options.successMessage);
    return new Promise((settle) => {
      setConfirmation({
        title: rewinds ? 'This file is already approved' : 'Start this step over?',
        paragraphs,
        confirmLabel: rewinds ? 'Change it' : 'Continue',
        run,
        successMessage: options.successMessage,
        blockOf: (liveFile, liveBatch) => fileEditBlock(liveFile, liveBatch),
        settle,
      });
    });
  }, [file, runGesture]);

  // Approving or un-approving one table, answering whether it was done.
  // Un-approving a table of an approved file, or of one in a check, un-approves
  // the file too, so it is confirmed first.
  const runUnitApproval = useCallback((run, { unitId: targetUnitId, kind, successMessage }) => {
    if (kind !== 'unapprove' || !needsRewindConfirmation(file)) return runGesture(run, successMessage);
    return new Promise((settle) => {
      setConfirmation({
        title: 'This file is already approved',
        paragraphs: [REWIND_CONFIRMATION],
        confirmLabel: 'Un-approve',
        run,
        successMessage,
        blockOf: (liveFile, liveBatch, liveUnits) => {
          const unit = liveUnits.find((candidate) => candidate.id === targetUnitId);
          if (!unit || !liveFile.units[targetUnitId]) return UNIT_REMOVED_NOTICE;
          if (!unit.approved) return 'This table is no longer approved.';
          return unitEditBlock(targetUnitId, { ...unit, approved: null }, liveFile, liveBatch, table);
        },
        settle,
      });
    });
  }, [file, table, runGesture]);

  const confirm = async () => {
    const { run, successMessage, settle } = confirmation;
    // Closed before the change runs: the change itself makes the file busy, and a
    // busy file closes an open confirmation as unsent.
    setConfirmation(null);
    settle(await runGesture(run, successMessage));
  };

  const cancelConfirmation = () => {
    setConfirmation(null);
    confirmation.settle(false);
  };

  // ── Waiting for the file ──────────────────────────────────────────────────
  const retry = () => setLoadAttempt((attempt) => attempt + 1);
  if (!file) {
    return (
      <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          {fileState.error
            ? <Alert severity="error" action={<Button color="inherit" onClick={retry}>Retry</Button>}>This file could not be loaded.</Alert>
            : <CircularProgress />}
        </DialogContent>
        <DialogActions><Button onClick={() => onClose()}>Close</Button></DialogActions>
      </Dialog>
    );
  }

  // ── What the file allows ──────────────────────────────────────────────────
  const editBlock = fileEditBlock(file, batch);
  const walkVerdicts = file.verdicts.filter((verdict) => verdict.process === 'walk');
  const showBatchPanel = Boolean(batch) && file.batch_status !== 'completed';
  // A unit whose ledger entry has not arrived yet has no edit block to show.
  const currentEntry = currentUnit ? file.units[currentUnit.id] : null;
  const footerNote = editBlock || (currentEntry ? unitEditBlock(currentUnit.id, currentUnit, file, batch, table) : '');
  const deleteBlock = fileDeleteBlock(file, batch);
  const deleteFailed = Boolean(file.deletion?.failed);

  // Approve file approves every table still to review only under a learned
  // profile; under a new one each table is approved by hand first, so Decode
  // learns its layout from the person (Spec 2 section 5.3).
  const ingestEntries = Object.values(file.units).filter((entry) => entry.disposition === 'ingest');
  const unapproved = Object.entries(file.units)
    .filter(([, entry]) => entry.disposition === 'ingest' && entry.status !== 'Ready');
  const approvable = unapproved
    .filter(([, entry]) => entry.status === 'PreviewReady' && entry.gate.length === 0 && !entry.pending);
  let approveFileBlock = '';
  if (file.approved) approveFileBlock = 'This file is approved.';
  else if (editBlock) approveFileBlock = editBlock;
  else if (file.status === 'Failed') {
    approveFileBlock = walkVerdicts.length > 0
      ? 'Fix the tables named above, then submit the file.'
      : 'Resolve the problems above first.';
  } else if (ingestEntries.length === 0) approveFileBlock = NO_INGEST_UNIT_LEFT;
  else if (unapproved.length > 0 && !(profile && isProfileLearned(profile))) {
    approveFileBlock = 'First upload from this profile: review and approve each table so Decode learns its layout';
  } else if (unapproved.length > approvable.length) {
    const notReady = unapproved.length - approvable.length;
    approveFileBlock = `${countNoun(notReady, 'table')} ${notReady === 1 ? 'is' : 'are'} not ready`;
  }
  const approveFileHint = approveFileBlock
    || (unapproved.length > 0 ? `Also approves ${unapproved.map(([, entry]) => entry.name).join(', ')}` : '');
  const showApproveFile = !file.batch_id && !file.deletion && file.status !== 'Cleaning';

  const handleApproveFile = async () => {
    setApprovingFile(true);
    await runGesture(
      () => approveFile(companyId, tableId, file.id),
      `${file.name} approved. It will be checked against ${tableName} shortly.`,
    );
    setApprovingFile(false);
  };

  const handleSubmit = async () => {
    setSubmittingFile(true);
    await runGesture(
      () => submitFile(companyId, tableId, file.id),
      'Submitted. Approve the file to have it checked again.',
    );
    setSubmittingFile(false);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { savedCount, failed } = await downloadFile(companyId, tableId, file);
      if (failed.length > 0) showToast(downloadFailureMessage(failed, savedCount));
    } catch (error) {
      console.error('Error downloading a file:', error);
      showToast(downloadFailureMessage([file.name]));
    } finally {
      setDownloading(false);
    }
  };

  const openDeleteConfirmation = () => {
    setDeleteError(null);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteFile(companyId, tableId, file.id);
      setDeleteOpen(false);
    } catch (error) {
      setDeleteError(gestureFailureMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  // ── The file view ─────────────────────────────────────────────────────────
  const fileView = (
    <>
      {file.verdicts.map((verdict, index) => (
        // Verdicts carry no id of their own; their position is stable between runs.
        // eslint-disable-next-line react/no-array-index-key
        <VerdictBanner key={index} verdict={verdict} units={units} />
      ))}
      {walkVerdicts.length > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 1 }}
          action={(
            <Button size="small" variant="contained" onClick={handleSubmit} disabled={file.status === 'Cleaning' || submittingFile}>
              Submit
            </Button>
          )}
        >
          Found when checking this file against {tableName}. Fix the tables named (un-approve, change, approve
          again), then submit the file once to have it checked again.
        </Alert>
      )}
      {batchError && <Alert severity="error" sx={{ mb: 1 }}>The check this file is in could not be loaded.</Alert>}
      {showBatchPanel && (
        // Spec 3 section 7.3: the panel opens the batch's questions itself, so it is given the table.
        <BatchPanel
          companyId={companyId}
          tableId={tableId}
          table={table}
          file={file}
          batch={batch}
          fileNames={fileNames}
          tableName={tableName}
          onNotice={showNotice}
        />
      )}
      {editBlock && file.status !== 'Cleaning' && !showBatchPanel && (
        <Alert
          severity="info"
          sx={{ mb: 1 }}
          action={deleteFailed ? (
            <Button size="small" color="inherit" onClick={openDeleteConfirmation}>Try again</Button>
          ) : undefined}
        >
          {editBlock}
        </Alert>
      )}
      {file.status === 'Cleaning' && Object.keys(file.processes).length > 0 && (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 1 }}>
          Decode is applying your change…
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1, mb: 1 }}>
        <Typography variant="h6">The file</Typography>
        <Typography variant="caption" color="text.secondary">
          what Decode decided, in order. Change one step and the steps after it are redone.
        </Typography>
      </Box>
      <FileSteps
        companyId={companyId}
        tableId={tableId}
        file={file}
        units={units}
        profile={profile}
        tableName={tableName}
        editBlock={editBlock}
        onFileChange={runFileChange}
        onOpenSheet={(sheet) => setWorkspace({ sheet, focusRegionId: null })}
      />
      <UnitCards
        companyId={companyId}
        tableId={tableId}
        file={file}
        units={units}
        table={table}
        batch={batch}
        currentUid={currentUser?.uid}
        fileEditBlock={editBlock}
        onOpenUnit={setUnitId}
        onViewFields={setFieldsUnitId}
        onFileChange={runFileChange}
        onUnitApproval={runUnitApproval}
      />
    </>
  );

  let reviewPane;
  if (unitsState.error) {
    reviewPane = (
      <Alert severity="error" action={<Button color="inherit" onClick={retry}>Retry</Button>}>
        The tables of this file could not be loaded.
      </Alert>
    );
  } else if (!unitsState.loaded) {
    reviewPane = <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  } else if (currentUnit) {
    reviewPane = (
      <UnitView
        companyId={companyId}
        tableId={tableId}
        file={file}
        unit={currentUnit}
        table={table}
        batch={batch}
        currentUid={currentUser?.uid}
        onBack={() => setUnitId(null)}
        onOpenSheet={(sheet, focusRegionId) => setWorkspace({ sheet, focusRegionId })}
        onUnitApproval={runUnitApproval}
        onFileChange={runFileChange}
        onToast={showToast}
      />
    );
  } else {
    reviewPane = fileView;
  }

  const profileLabel = profile?.name || formatText(file.data_profile_id);

  return (
    <>
      <Dialog
        open={open}
        onClose={() => onClose()}
        maxWidth={false}
        PaperProps={{ sx: { width: '96vw', maxWidth: '96vw', height: '94vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', py: 1.25 }}>
          <FileStatusIcon status={file.status} />
          <Typography variant="subtitle1" fontWeight={700} component="span">{file.name}</Typography>
          <Chip size="small" color={fileStatusColour(file.status)} label={fileStatusLabel(file.status)} />
          <Typography variant="caption" color="text.secondary" component="span">
            {profileLabel} · uploaded {formatTimestamp(file.created_at)}
            {file.date ? ` · reporting ${formatDateOnly(file.date)}` : ''}
            {file.approved ? ` · ${approvalLine(file.approved, currentUser?.uid)}` : ''}
          </Typography>
          {Object.entries(file.metadata || {}).map(([category, value]) => (
            <Chip key={category} size="small" variant="outlined" label={`${formatText(category)}: ${formatDisplayValue(value)}`} />
          ))}
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            startIcon={downloading ? <CircularProgress size={14} /> : <DownloadIcon fontSize="small" />}
            onClick={handleDownload}
            disabled={downloading}
          >
            Download
          </Button>
          <IconButton size="small" aria-label="Close" onClick={() => onClose()}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, display: 'flex', overflow: 'hidden' }}>
          <FilePane companyId={companyId} tableId={tableId} file={file} units={units} focusUnit={currentUnit} />
          <Box ref={reviewPaneRef} data-testid="review-pane" sx={{ flex: 1, minWidth: 0, overflowY: 'auto', p: 2 }}>
            {reviewPane}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1, gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{footerNote}</Typography>
          <Tooltip title={deleteBlock}>
            <span>
              <Button color="error" onClick={openDeleteConfirmation} disabled={Boolean(deleteBlock)}>
                {deleteFailed ? 'Try again' : 'Delete file'}
              </Button>
            </span>
          </Tooltip>
          {walkVerdicts.length > 0 && (
            <Button variant="contained" onClick={handleSubmit} disabled={file.status === 'Cleaning' || submittingFile}>
              Submit
            </Button>
          )}
          {currentUnit && <Button variant="outlined" onClick={() => setUnitId(null)}>Back to the file</Button>}
          {showApproveFile && (
            <Tooltip title={approveFileHint}>
              <span>
                <Button
                  variant="contained"
                  onClick={handleApproveFile}
                  disabled={Boolean(approveFileBlock) || approvingFile}
                >
                  {file.approved ? 'Approved' : 'Approve file'}
                </Button>
              </span>
            </Tooltip>
          )}
          <Button variant="outlined" onClick={() => onClose()}>Close</Button>
        </DialogActions>
      </Dialog>

      {workspace && (
        <SheetWorkspace
          open
          companyId={companyId}
          tableId={tableId}
          file={file}
          sheetName={workspace.sheet}
          batch={batch}
          focusRegionId={workspace.focusRegionId}
          setAside={setAsideSheetDrafts[workspace.sheet] || null}
          onSetAside={(saved) => setSetAsideSheetDrafts((previous) => ({ ...previous, [workspace.sheet]: saved }))}
          onNotice={showNotice}
          onClose={() => setWorkspace(null)}
        />
      )}

      {fieldsUnit && <FieldsDialog open unit={fieldsUnit} onClose={() => setFieldsUnitId(null)} />}

      <Dialog open={Boolean(confirmation)} onClose={cancelConfirmation} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmation?.title}</DialogTitle>
        <DialogContent>
          {(confirmation?.paragraphs || []).map((paragraph) => (
            <DialogContentText key={paragraph} sx={{ mb: 1 }}>{paragraph}</DialogContentText>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelConfirmation}>Cancel</Button>
          <Button variant="contained" onClick={confirm}>{confirmation?.confirmLabel}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={noIngestOpen} onClose={() => setNoIngestOpen(false)} maxWidth="xs" fullWidth>
        <DialogContent>
          <DialogContentText>{NO_INGEST_UNIT_LEFT}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoIngestOpen(false)}>Dismiss</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)}>
        <DialogTitle>Delete this file?</DialogTitle>
        <DialogContent>
          <DialogContentText>{deleteConfirmationText(file, tableName)}</DialogContentText>
          {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={toast?.severity === 'success' ? 2600 : 4200}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">{toast.message}</Alert>
        ) : <span />}
      </Snackbar>
    </>
  );
}

export default FileReviewDialog;
